"use client";

import { useEffect, useRef } from "react";
import { cn } from "@/src/lib/utils";

interface CursorTrackAreaProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Exposes the mouse position within this element as `--cursor-x`/`--cursor-y`
 * CSS custom properties (and `--cursor-visible`, `--cursor-transition-duration`)
 * for a `CursorFollowBadge` child to read.
 *
 * Scrolling moves this element under a cursor that hasn't itself moved, so
 * neither `mousemove` nor `mouseleave` fires — the badge would otherwise
 * stay stuck at its last position/visibility. To handle that, the last
 * known viewport cursor point is re-checked against this element's
 * (possibly now-scrolled) bounding box on every scroll, updating both
 * position and visibility to match reality.
 *
 * Hiding is always instant (no fade lag on leave); showing fades in
 * smoothly — `--cursor-transition-duration` carries that asymmetry so the
 * badge never lingers after the cursor has actually left.
 *
 * Mouse listeners here don't intercept clicks, so this is safe to wrap
 * around a `<Link>`'s content.
 */
export function CursorTrackArea({ children, className }: CursorTrackAreaProps) {
  const ref = useRef<HTMLDivElement>(null);
  const lastClientPos = useRef<{ x: number; y: number } | null>(null);

  const setVisible = (element: HTMLElement, visible: boolean) => {
    element.style.setProperty("--cursor-visible", visible ? "1" : "0");
    element.style.setProperty("--cursor-transition-duration", visible ? "300ms" : "0ms");
  };

  useEffect(() => {
    const reconcile = () => {
      const element = ref.current;
      const point = lastClientPos.current;
      if (!element || !point) return;

      const bounds = element.getBoundingClientRect();
      const inside =
        point.x >= bounds.left &&
        point.x <= bounds.right &&
        point.y >= bounds.top &&
        point.y <= bounds.bottom;

      setVisible(element, inside);
      if (inside) {
        element.style.setProperty("--cursor-x", `${point.x - bounds.left}px`);
        element.style.setProperty("--cursor-y", `${point.y - bounds.top}px`);
      }
    };

    window.addEventListener("scroll", reconcile, { passive: true, capture: true });
    window.addEventListener("resize", reconcile);

    return () => {
      window.removeEventListener("scroll", reconcile, { capture: true });
      window.removeEventListener("resize", reconcile);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={cn("relative", className)}
      onMouseMove={(event) => {
        lastClientPos.current = { x: event.clientX, y: event.clientY };

        const element = ref.current;
        if (!element) return;
        const bounds = element.getBoundingClientRect();
        setVisible(element, true);
        element.style.setProperty("--cursor-x", `${event.clientX - bounds.left}px`);
        element.style.setProperty("--cursor-y", `${event.clientY - bounds.top}px`);
      }}
      onMouseLeave={() => {
        lastClientPos.current = null;
        const element = ref.current;
        if (element) setVisible(element, false);
      }}
    >
      {children}
    </div>
  );
}